/**
 * Report Service
 * 
 * Service สำหรับดึงข้อมูลรายงาน
 * ใช้ batch loading เหมือน Angular
 */

import { queryClass } from '@/services/adapters/parse/parse-client'
import type {
  MonthlyReportParams,
  MonthlyReportData,
  DailyTableData,
  FinancialStatistics,
  UserStatistics,
  ChartData,
} from './report.types'
import moment from 'moment'

// ============================================
// Helper: Batch Loading (เหมือน Angular)
// ============================================

async function getAllTransactions(
  sessionToken: string,
  queryOptions: any
): Promise<any[]> {
  const allResults: any[] = []
  const batchSize = 1000
  let skip = 0

  console.log('📊 [ReportService] Starting batch loading...', {
    where: queryOptions.where,
    batchSize,
  })

  while (true) {
    try {
      const batch = await queryClass('DepositWithdraw', {
        ...queryOptions,
        skip,
        limit: batchSize,
      }, sessionToken)

      const results = batch.results || []
      console.log(`📦 Batch ${Math.floor(skip / batchSize) + 1}: Found ${results.length} records (total: ${batch.count || 0})`)

      if (results.length === 0) break

      // Log sample data from first batch
      if (skip === 0 && results.length > 0) {
        const sample = results[0]
        console.log('📋 Sample record:', {
          objectId: sample.objectId,
          amount: sample.amount,
          type: sample.type,
          status: sample.status,
          commission: sample.commission,
          isBonus: sample.isBonus,
          createdAt: sample.createdAt,
          hasGetMethod: typeof sample.get === 'function',
        })
      }

      allResults.push(...results)
      skip += batchSize

      // ป้องกันไม่ให้วนไม่รู้จบ
      if (skip > 100000) {
        console.warn('⚠️ Reached maximum limit of 100,000 records')
        break
      }
      
      // ถ้า results น้อยกว่า batchSize แสดงว่าเป็น batch สุดท้าย
      if (results.length < batchSize) {
        break
      }
    } catch (error) {
      console.error(`❌ Error in batch ${Math.floor(skip / batchSize) + 1}:`, error)
      break
    }
  }

  console.log(`✅ Total records loaded: ${allResults.length}`)
  return allResults
}

// ============================================
// Helper: Group Transactions by Date
// ============================================

function groupTransactionsByDate(transactions: any[]): { [date: string]: any[] } {
  const groups: { [date: string]: any[] } = {}

  transactions.forEach((transaction) => {
    // ใช้ timezone +07:00 เหมือน Angular (moment(transaction.createdAt).utcOffset('+07:00'))
    // รองรับทั้ง Parse Object และ plain object
    const createdAt = transaction.createdAt || transaction.get?.('createdAt')
    if (!createdAt) {
      console.warn('⚠️ Transaction missing createdAt:', transaction)
      return
    }
    
    // ใช้วิธีเดียวกับ Angular: moment(transaction.createdAt).utcOffset('+07:00').format('YYYY-MM-DD')
    const localDate = moment(createdAt)
      .utcOffset('+07:00')
      .format('YYYY-MM-DD')

    if (!groups[localDate]) {
      groups[localDate] = []
    }
    groups[localDate].push(transaction)
  })

  return groups
}

// ============================================
// Helper: Check if Admin/Manual Transaction
// ============================================

/**
 * ✅ V2: ตรวจสอบว่าเป็น Admin/Manual transaction หรือไม่
 * รองรับทั้ง V1 และ V2:
 * - V1: createFrom === 'admin'
 * - V2: createFrom === 'Panel' || createFrom === 'WebAdmin'
 */
function isAdminTransaction(createFrom: string | null | undefined): boolean {
  if (!createFrom) return false
  return createFrom === 'admin' || createFrom === 'Panel' || createFrom === 'WebAdmin'
}

/**
 * ✅ V2: ตรวจสอบว่าเป็น Manual withdraw หรือไม่
 * รองรับทั้ง typo และถูกต้อง:
 * - 'Manaul' (typo - legacy)
 * - 'Manual' (ถูกต้อง)
 */
function isManualWithdraw(withdrawBy: string | null | undefined): boolean {
  if (!withdrawBy) return false
  const normalized = withdrawBy.toLowerCase()
  return normalized === 'manual' || normalized === 'manaul' // รองรับ typo
}

// ============================================
// Helper: Calculate Financial Stats
// ============================================

function calculateFinancialStats(results: any[]): FinancialStatistics {
  const stats: FinancialStatistics = {
    totalDepositAmount: 0,
    totalDepositBonusAmount: 0,
    totalDepositRegularAmount: 0,
    totalWithdrawAmount: 0,
    totalWithdrawBonusAmount: 0,
    totalWithdrawRegularAmount: 0,
    totalReturnToMemberAmount: 0,
    netProfit: 0,
    adminDepositAmount: 0,
    autoDepositAmount: 0,
    adminWithdrawAmount: 0,
    autoWithdrawAmount: 0,
    totalTransactions: results.length,
    depositTransactions: 0,
    withdrawTransactions: 0,
    pendingTransactions: 0,
    approvedTransactions: 0,
  }

  let cashDepositTotal = 0
  let cashWithdrawTotal = 0
  let bonusDepositTotal = 0
  let returnToMemberTotal = 0

  console.log('📊 [calculateFinancialStats] Processing', results.length, 'transactions')
  
  results.forEach((item, index) => {
    // Angular ใช้ item.get('amount') แต่เราใช้ REST API ที่ส่งมาเป็น plain JSON
    // รองรับทั้งสองแบบ: Parse Object (.get()) และ plain object (direct property)
    const getValue = (key: string) => {
      if (typeof item.get === 'function') {
        return item.get(key)
      }
      return item[key]
    }
    
    const amount = getValue('amount') || 0
    const type = getValue('type')
    const isBonus = getValue('isBonus') === true
    const returnToMember = getValue('return_to_member') === true
    const withdrawBy = getValue('withdrawBy')
    const createFrom = getValue('create_from')
    const status = getValue('status')
    const commission = getValue('commission') === true

    // Log first few items for debugging
    if (index < 3) {
      console.log(`📋 Transaction ${index + 1}:`, {
        amount,
        type,
        isBonus,
        returnToMember,
        status,
        commission,
        createFrom,
        withdrawBy,
        hasGetMethod: typeof item.get === 'function',
        directAmount: item.amount,
      })
    }

    // Angular ไม่ได้ skip commission transactions ใน calculateFinancialStats
    // แต่ query filter commission: false อยู่แล้ว
    // ดังนั้นเราไม่ต้อง skip ที่นี่

    stats.approvedTransactions++

    // คำนวณคืนยอดเสีย
    if (returnToMember === true && type === 'Deposit' && isBonus === false) {
      stats.totalReturnToMemberAmount += amount
      returnToMemberTotal += amount
    }
    // ฝากเงินสดปกติ (ไม่รวมคืนยอดเสีย)
    else if (type === 'Deposit' && isBonus === false) {
      stats.depositTransactions++
      stats.totalDepositAmount += amount
      stats.totalDepositRegularAmount += amount
      cashDepositTotal += amount

      // ✅ V2: รองรับทั้ง V1 ('admin') และ V2 ('Panel', 'WebAdmin')
      if (isAdminTransaction(createFrom)) {
        stats.adminDepositAmount += amount
      } else {
        stats.autoDepositAmount += amount
      }
    } else if (type === 'Deposit' && isBonus === true) {
      stats.depositTransactions++
      stats.totalDepositAmount += amount
      stats.totalDepositBonusAmount += amount
      bonusDepositTotal += amount

      // ✅ V2: รองรับทั้ง V1 ('admin') และ V2 ('Panel', 'WebAdmin')
      if (isAdminTransaction(createFrom)) {
        stats.adminDepositAmount += amount
      } else {
        stats.autoDepositAmount += amount
      }
    } else if (type === 'Withdraw' && isBonus === false) {
      stats.withdrawTransactions++
      stats.totalWithdrawAmount += amount
      stats.totalWithdrawRegularAmount += amount
      cashWithdrawTotal += amount

      // ✅ V2: รองรับทั้ง 'manual'/'Manual'/'Manaul' และ 'admin'/'Panel'/'WebAdmin'
      if (isManualWithdraw(withdrawBy) || isAdminTransaction(createFrom)) {
        stats.adminWithdrawAmount += amount
      } else {
        stats.autoWithdrawAmount += amount
      }
    } else if (type === 'Withdraw' && isBonus === true) {
      stats.withdrawTransactions++
      stats.totalWithdrawAmount += amount
      stats.totalWithdrawBonusAmount += amount

      // ✅ V2: รองรับทั้ง 'manual'/'Manual'/'Manaul' และ 'admin'/'Panel'/'WebAdmin'
      if (isManualWithdraw(withdrawBy) || isAdminTransaction(createFrom)) {
        stats.adminWithdrawAmount += amount
      } else {
        stats.autoWithdrawAmount += amount
      }
    }
  })

  // ปรับสูตรกำไรสุทธิให้รวมคืนยอดเสีย
  stats.netProfit = cashDepositTotal - (cashWithdrawTotal + bonusDepositTotal + returnToMemberTotal)
  
  console.log('📊 [calculateFinancialStats] Results:', {
    totalTransactions: stats.totalTransactions,
    totalDepositRegularAmount: stats.totalDepositRegularAmount,
    totalWithdrawRegularAmount: stats.totalWithdrawRegularAmount,
    totalDepositBonusAmount: stats.totalDepositBonusAmount,
    totalReturnToMemberAmount: stats.totalReturnToMemberAmount,
    netProfit: stats.netProfit,
    cashDepositTotal,
    cashWithdrawTotal,
    bonusDepositTotal,
    returnToMemberTotal,
  })
  
  return stats
}

// ============================================
// Service
// ============================================

export const ReportService = {
  /**
   * ดึงรายงานรายเดือน
   */
  async getMonthlyReport(
    sessionToken: string,
    params: MonthlyReportParams
  ): Promise<MonthlyReportData> {
    console.log('📊 [ReportService] getMonthlyReport:', params)

    // สร้าง date range - ใช้วิธีเดียวกับ Angular
    // Angular: moment().year().month().startOf('month').toDate() แล้วค่อยแปลงเป็น UTC
    const startOfMonth = moment()
      .year(params.year)
      .month(params.month - 1)
      .startOf('month')
    const endOfMonth = moment()
      .year(params.year)
      .month(params.month - 1)
      .endOf('month')
    
    // แปลงเป็น Date object ก่อน (เหมือน Angular: this.params.startDate = startOfMonth.toDate())
    const startDateLocal = startOfMonth.toDate()
    const endDateLocal = endOfMonth.toDate()
    
    // แล้วค่อยแปลงเป็น UTC (เหมือน Angular: moment(this.params.startDate).startOf('day').utc().toDate())
    const startUTC = moment(startDateLocal).startOf('day').utc().toDate()
    const endUTC = moment(endDateLocal).endOf('day').utc().toDate()

    console.log('📅 Date Range:', {
      localStart: startOfMonth.format('YYYY-MM-DD HH:mm:ss'),
      localEnd: endOfMonth.format('YYYY-MM-DD HH:mm:ss'),
      startDateLocal: startDateLocal.toISOString(),
      endDateLocal: endDateLocal.toISOString(),
      startUTC: startUTC.toISOString(),
      endUTC: endUTC.toISOString(),
      year: params.year,
      month: params.month,
    })

    // Load all data in parallel (ไม่ต้องดึง userData แล้ว - ย้ายไป Members Report)
    const [financialData, dailyTableData, chartData] = await Promise.all([
      this.loadFinancialStatistics(sessionToken, startUTC, endUTC),
      this.loadDailyTableData(sessionToken, params, startUTC, endUTC),
      this.loadChartData(sessionToken, params, startUTC, endUTC),
    ])

    return {
      financial: financialData,
      users: {
        totalMembers: 0,
        totalNewMembers: 0,
        newMembersWithDeposit: 0,
        activeMembers: 0,
      }, // Empty user data - ย้ายไป Members Report แล้ว
      dailyTable: dailyTableData,
      chartData,
    }
  },

  /**
   * ดึงรายงานเดือนก่อนหน้า (สำหรับเปรียบเทียบ)
   */
  async getPreviousMonthReport(
    sessionToken: string,
    params: MonthlyReportParams
  ): Promise<FinancialStatistics | null> {
    try {
      const prevMonth = moment()
        .year(params.year)
        .month(params.month - 1)
        .subtract(1, 'month')

      const prevStartOfMonth = prevMonth.clone().startOf('month')
      const prevEndOfMonth = prevMonth.clone().endOf('month')
      
      const prevStartDateLocal = prevStartOfMonth.toDate()
      const prevEndDateLocal = prevEndOfMonth.toDate()
      
      const startUTC = moment(prevStartDateLocal).startOf('day').utc().toDate()
      const endUTC = moment(prevEndDateLocal).endOf('day').utc().toDate()

      return await this.loadFinancialStatistics(sessionToken, startUTC, endUTC)
    } catch (error) {
      console.error('❌ Error loading previous month report:', error)
      return null
    }
  },

  /**
   * ดึงสถิติการเงิน
   */
  async loadFinancialStatistics(
    sessionToken: string,
    startDate: Date,
    endDate: Date
  ): Promise<FinancialStatistics> {
    try {
      // ใช้ query format เหมือน Angular
      // Parse Server ต้องการ Date object ในรูปแบบพิเศษ
      // ใช้ __type: 'Date', iso: ISO string format
      // ✅ V2: รองรับทั้ง 'Approved' (V1) และ 'Completed' (V2)
      const queryOptions = {
        where: {
          status: { $in: ['Approved', 'Completed'] }, // ✅ V1: Approved, V2: Completed
          commission: false,
          deletedAt: { $exists: false },
          createdAt: {
            $gte: {
              __type: 'Date',
              iso: startDate.toISOString(),
            },
            $lte: {
              __type: 'Date',
              iso: endDate.toISOString(),
            },
          },
        },
        order: 'createdAt', // ascending เหมือน Angular
      }

      // Debug: ตรวจสอบ query options
      console.log('🔍 [loadFinancialStatistics] Query options:', {
        where: JSON.stringify(queryOptions.where, null, 2),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startDateLocal: moment(startDate).format('YYYY-MM-DD HH:mm:ss Z'),
        endDateLocal: moment(endDate).format('YYYY-MM-DD HH:mm:ss Z'),
        startDateType: typeof startDate,
        endDateType: typeof endDate,
        startDateValue: startDate,
        endDateValue: endDate,
      })
      
      // Debug: ตรวจสอบข้อมูลตัวอย่างเพื่อดู createdAt format และเปรียบเทียบกับ date range
      const sampleQuery = {
        where: {
          status: { $in: ['Approved', 'Completed'] }, // ✅ V1: Approved, V2: Completed
          commission: false,
          deletedAt: { $exists: false },
        },
        limit: 10,
        order: '-createdAt',
      }
      const sampleResults = await queryClass('DepositWithdraw', sampleQuery, sessionToken)
      if (sampleResults.results && sampleResults.results.length > 0) {
        console.log('📋 Sample records (no date filter):', sampleResults.results.map((r: any) => {
          const createdAt = r.createdAt ? new Date(r.createdAt) : null
          const isInRange = createdAt && createdAt >= startDate && createdAt <= endDate
          return {
            objectId: r.objectId,
            createdAt: r.createdAt,
            createdAtISO: createdAt ? createdAt.toISOString() : null,
            createdAtLocal: createdAt ? moment(createdAt).format('YYYY-MM-DD HH:mm:ss Z') : null,
            amount: r.amount,
            type: r.type,
            isInRange,
            diffFromStart: createdAt ? (createdAt.getTime() - startDate.getTime()) / (1000 * 60 * 60) : null, // hours
            diffFromEnd: createdAt ? (endDate.getTime() - createdAt.getTime()) / (1000 * 60 * 60) : null, // hours
          }
        }))
        
        // ตรวจสอบว่ามีข้อมูลในเดือนที่เลือกหรือไม่ (ใช้ startDate แทน params)
        const selectedMonth = moment(startDate)
        const recordsInMonth = sampleResults.results.filter((r: any) => {
          if (!r.createdAt) return false
          const recordDate = moment(r.createdAt)
          return recordDate.year() === selectedMonth.year() && recordDate.month() === selectedMonth.month()
        })
        console.log('📊 Records in selected month:', {
          selectedMonth: selectedMonth.format('YYYY-MM'),
          found: recordsInMonth.length,
          records: recordsInMonth.map((r: any) => ({
            date: moment(r.createdAt).format('YYYY-MM-DD'),
            amount: r.amount,
            type: r.type,
          })),
        })
      }

      const results = await getAllTransactions(sessionToken, queryOptions)
      
      // Debug: ตรวจสอบว่ามีข้อมูลหรือไม่
      if (results.length === 0) {
        console.warn('⚠️ [loadFinancialStatistics] No results found!', {
          queryOptions,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        })
        
        // ลอง query โดยไม่ใส่ date range เพื่อดูว่ามีข้อมูลหรือไม่
        const testQuery = {
          where: {
            // ✅ V2: รองรับทั้ง 'Approved' (V1) และ 'Completed' (V2)
            status: { $in: ['Approved', 'Completed'] },
            commission: false,
            deletedAt: { $exists: false },
          },
          limit: 5,
        }
        const testResults = await queryClass('DepositWithdraw', testQuery, sessionToken)
        console.log('🔍 [loadFinancialStatistics] Test query (no date range):', {
          count: testResults.count,
          sampleDates: testResults.results?.slice(0, 3).map((r: any) => ({
            createdAt: r.createdAt,
            amount: r.amount,
            type: r.type,
          })),
        })
      }
      
      console.log('📊 [loadFinancialStatistics] Results:', {
        totalResults: results.length,
        firstItem: results[0] ? {
          objectId: results[0].objectId,
          amount: results[0].amount,
          type: results[0].type,
          isBonus: results[0].isBonus,
          status: results[0].status,
          commission: results[0].commission,
          createdAt: results[0].createdAt,
        } : null,
        lastItem: results.length > 0 ? {
          objectId: results[results.length - 1].objectId,
          amount: results[results.length - 1].amount,
          type: results[results.length - 1].type,
          createdAt: results[results.length - 1].createdAt,
        } : null,
      })
      
      return calculateFinancialStats(results)
    } catch (error) {
      console.error('❌ Error loading financial statistics:', error)
      return {
        totalDepositAmount: 0,
        totalDepositBonusAmount: 0,
        totalDepositRegularAmount: 0,
        totalWithdrawAmount: 0,
        totalWithdrawBonusAmount: 0,
        totalWithdrawRegularAmount: 0,
        totalReturnToMemberAmount: 0,
        netProfit: 0,
        adminDepositAmount: 0,
        autoDepositAmount: 0,
        adminWithdrawAmount: 0,
        autoWithdrawAmount: 0,
        totalTransactions: 0,
        depositTransactions: 0,
        withdrawTransactions: 0,
        pendingTransactions: 0,
        approvedTransactions: 0,
      }
    }
  },

  /**
   * ดึงสถิติสมาชิก
   */
  async loadUserStatistics(
    sessionToken: string,
    startDate: Date,
    endDate: Date
  ): Promise<UserStatistics> {
    try {
      // Total members
      const totalMembersResult = await queryClass(
        '_User',
        {
          where: {
            type: 'customer',
            deletedAt: { $exists: false },
          },
          count: 1,
        },
        sessionToken
      )
      const totalMembers = totalMembersResult.count || 0

      // New members in period
      const newMembersResult = await queryClass(
        '_User',
        {
          where: {
            type: 'customer',
            deletedAt: { $exists: false },
            createdAt: {
              $gte: {
                __type: 'Date',
                iso: startDate.toISOString(),
              },
              $lte: {
                __type: 'Date',
                iso: endDate.toISOString(),
              },
            },
          },
        },
        sessionToken
      )
      const newMembers = newMembersResult.results || []
      const totalNewMembers = newMembers.length

      // New members with deposit
      let newMembersWithDeposit = 0
      if (newMembers.length > 0) {
        const memberIds = newMembers.map((m: any) => m.objectId)
        const depositResult = await queryClass(
          'DepositWithdraw',
          {
            where: {
              type: 'Deposit',
              status: { $in: ['Approved', 'Completed'] }, // ✅ V1: Approved, V2: Completed
              isBonus: false,
              commission: false,
              return_to_member: false,
              amount: { $gte: 20 },
              member_id: {
                $in: memberIds.map((id: string) => ({
                  __type: 'Pointer',
                  className: '_User',
                  objectId: id,
                })),
              },
              deletedAt: { $exists: false },
            },
          },
          sessionToken
        )

        const uniqueDepositorIds = new Set(
          (depositResult.results || []).map((d: any) => d.member_id?.objectId).filter(Boolean)
        )
        newMembersWithDeposit = uniqueDepositorIds.size
      }

      // Active members (from Session or transactions)
      let activeMembers = 0
      try {
        const sessionResult = await queryClass(
          'Session',
          {
            where: {
              createdAt: {
                $gte: {
                  __type: 'Date',
                  iso: startDate.toISOString(),
                },
                $lte: {
                  __type: 'Date',
                  iso: endDate.toISOString(),
                },
              },
            },
          },
          sessionToken
        )

        const activeUserIds = new Set(
          (sessionResult.results || [])
            .map((s: any) => s.user?.objectId)
            .filter(Boolean)
        )
        activeMembers = activeUserIds.size
      } catch (sessionError) {
        console.warn('⚠️ Error loading active members from Session, using transactions:', sessionError)
        // Fallback: use transactions
        const activeResult = await queryClass(
          'DepositWithdraw',
          {
            where: {
              status: { $in: ['Approved', 'Completed'] }, // ✅ V1: Approved, V2: Completed
              commission: false,
              return_to_member: false,
              createdAt: {
                $gte: {
                  __type: 'Date',
                  iso: startDate.toISOString(),
                },
                $lte: {
                  __type: 'Date',
                  iso: endDate.toISOString(),
                },
              },
              deletedAt: { $exists: false },
            },
          },
          sessionToken
        )

        const activeMemberIds = new Set(
          (activeResult.results || []).map((t: any) => t.member_id?.objectId).filter(Boolean)
        )
        activeMembers = activeMemberIds.size
      }

      return {
        totalMembers,
        totalNewMembers,
        newMembersWithDeposit,
        activeMembers,
      }
    } catch (error) {
      console.error('❌ Error loading user statistics:', error)
      return {
        totalMembers: 0,
        totalNewMembers: 0,
        newMembersWithDeposit: 0,
        activeMembers: 0,
      }
    }
  },

  /**
   * ดึงข้อมูลตารางรายวัน
   */
  async loadDailyTableData(
    sessionToken: string,
    params: MonthlyReportParams,
    startDate: Date,
    endDate: Date
  ): Promise<DailyTableData[]> {
    try {
      const queryOptions = {
        where: {
          // ✅ V2: รองรับทั้ง 'Approved' (V1) และ 'Completed' (V2)
          status: { $in: ['Approved', 'Completed'] },
          commission: false,
          deletedAt: { $exists: false },
          createdAt: {
            $gte: {
              __type: 'Date',
              iso: startDate.toISOString(),
            },
            $lte: {
              __type: 'Date',
              iso: endDate.toISOString(),
            },
          },
        },
        order: 'createdAt',
      }

      const results = await getAllTransactions(sessionToken, queryOptions)

      // สร้างรายการวันที่ทั้งหมดในเดือนที่เลือก
      const startOfMonth = moment().year(params.year).month(params.month - 1).startOf('month')
      const endOfMonth = moment().year(params.year).month(params.month - 1).endOf('month')
      const today = moment().startOf('day')
      const allDates: string[] = []

      let currentDate = startOfMonth.clone()
      while (currentDate.isSameOrBefore(endOfMonth, 'day')) {
        // แสดงเฉพาะวันที่ที่ผ่านมาแล้ว (ไม่แสดงวันอนาคต)
        if (currentDate.isSameOrBefore(today, 'day')) {
          allDates.push(currentDate.format('YYYY-MM-DD'))
        }
        currentDate.add(1, 'day')
      }

      const dateGroups = groupTransactionsByDate(results)
      const tableData: DailyTableData[] = []

      // Helper function to get value (รองรับทั้ง Parse Object และ plain object)
      const getValue = (obj: any, key: string) => {
        if (typeof obj.get === 'function') {
          return obj.get(key)
        }
        return obj[key]
      }

      allDates.forEach((date) => {
        const dayTransactions = dateGroups[date] || []

        // ใช้วิธีเดียวกับ Angular: t.get('type') === 'Deposit' && t.get('isBonus') === false && t.get('return_to_member') !== true
        const cashDepositAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            const returnToMember = getValue(t, 'return_to_member') === true
            return type === 'Deposit' && isBonus === false && returnToMember !== true
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const bonusDepositAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            return type === 'Deposit' && isBonus === true
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        // Angular: t.get('return_to_member') === true && t.get('type') === 'Deposit' && t.get('isBonus') === false && t.get('commission') === false
        const returnToMemberAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            const returnToMember = getValue(t, 'return_to_member') === true
            const commission = getValue(t, 'commission') === true
            return returnToMember === true && type === 'Deposit' && isBonus === false && commission === false
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const cashWithdrawAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            return type === 'Withdraw' && isBonus === false
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const depositCount = dayTransactions.filter((t) => {
          return getValue(t, 'type') === 'Deposit'
        }).length
        const withdrawCount = dayTransactions.filter((t) => {
          return getValue(t, 'type') === 'Withdraw'
        }).length

        const adminDepositAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const createFrom = getValue(t, 'create_from')
            return type === 'Deposit' && isAdminTransaction(createFrom)
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const autoDepositAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const createFrom = getValue(t, 'create_from')
            return type === 'Deposit' && !isAdminTransaction(createFrom)
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const adminWithdrawAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const withdrawBy = getValue(t, 'withdrawBy')
            const createFrom = getValue(t, 'create_from')
            return type === 'Withdraw' && (isManualWithdraw(withdrawBy) || isAdminTransaction(createFrom))
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const autoWithdrawAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const withdrawBy = getValue(t, 'withdrawBy')
            const createFrom = getValue(t, 'create_from')
            return type === 'Withdraw' && !isManualWithdraw(withdrawBy) && !isAdminTransaction(createFrom)
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const netCashFlow = cashDepositAmount - cashWithdrawAmount
        const totalProfit = cashDepositAmount - (cashWithdrawAmount + bonusDepositAmount + returnToMemberAmount)

        tableData.push({
          date,
          displayDate: moment(date).format('DD/MM/YYYY'),
          cashDeposit: cashDepositAmount,
          cashWithdraw: cashWithdrawAmount,
          bonusDeposit: bonusDepositAmount,
          returnToMember: returnToMemberAmount,
          netCashFlow,
          totalProfit,
          depositCount,
          withdrawCount,
          totalTransactionCount: dayTransactions.length,
          adminDepositAmount,
          autoDepositAmount,
          adminWithdrawAmount,
          autoWithdrawAmount,
        })
      })

      return tableData
    } catch (error) {
      console.error('❌ Error loading daily table data:', error)
      return []
    }
  },

  /**
   * ดึงข้อมูลกราฟ
   */
  async loadChartData(
    sessionToken: string,
    params: MonthlyReportParams,
    startDate: Date,
    endDate: Date
  ): Promise<ChartData> {
    try {
      const queryOptions = {
        where: {
          // ✅ V2: รองรับทั้ง 'Approved' (V1) และ 'Completed' (V2)
          status: { $in: ['Approved', 'Completed'] },
          commission: false,
          deletedAt: { $exists: false },
          createdAt: {
            $gte: {
              __type: 'Date',
              iso: startDate.toISOString(),
            },
            $lte: {
              __type: 'Date',
              iso: endDate.toISOString(),
            },
          },
        },
        order: 'createdAt',
      }

      const results = await getAllTransactions(sessionToken, queryOptions)

      // สร้างรายการวันที่ทั้งหมดในเดือนที่เลือก
      const startOfMonth = moment().year(params.year).month(params.month - 1).startOf('month')
      const endOfMonth = moment().year(params.year).month(params.month - 1).endOf('month')
      const today = moment().startOf('day')
      const allDates: string[] = []

      let currentDate = startOfMonth.clone()
      while (currentDate.isSameOrBefore(endOfMonth, 'day')) {
        // แสดงเฉพาะวันที่ที่ผ่านมาแล้ว
        if (currentDate.isSameOrBefore(today, 'day')) {
          allDates.push(currentDate.format('YYYY-MM-DD'))
        }
        currentDate.add(1, 'day')
      }

      const dateGroups = groupTransactionsByDate(results)

      // Helper function to get value (รองรับทั้ง Parse Object และ plain object)
      const getValue = (obj: any, key: string) => {
        if (typeof obj.get === 'function') {
          return obj.get(key)
        }
        return obj[key]
      }

      const cashDepositData: { x: number; y: number }[] = []
      const bonusDepositData: { x: number; y: number }[] = []
      const returnToMemberData: { x: number; y: number }[] = []
      const cashWithdrawData: { x: number; y: number }[] = []
      const profitData: { x: number; y: number }[] = []

      allDates.forEach((date) => {
        const dayTransactions = dateGroups[date] || []

        // ใช้วิธีเดียวกับ Angular
        const cashDepositAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            const returnToMember = getValue(t, 'return_to_member') === true
            return type === 'Deposit' && isBonus === false && returnToMember !== true
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const bonusDepositAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            return type === 'Deposit' && isBonus === true
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const returnToMemberAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            const returnToMember = getValue(t, 'return_to_member') === true
            const commission = getValue(t, 'commission') === true
            return returnToMember === true && type === 'Deposit' && isBonus === false && commission === false
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const cashWithdrawAmount = dayTransactions
          .filter((t) => {
            const type = getValue(t, 'type')
            const isBonus = getValue(t, 'isBonus') === true
            return type === 'Withdraw' && isBonus === false
          })
          .reduce((sum, t) => sum + (getValue(t, 'amount') || 0), 0)

        const dailyProfit = cashDepositAmount - (cashWithdrawAmount + bonusDepositAmount + returnToMemberAmount)
        const dateTimestamp = moment(date).valueOf()

        cashDepositData.push({ x: dateTimestamp, y: cashDepositAmount })
        bonusDepositData.push({ x: dateTimestamp, y: bonusDepositAmount })
        returnToMemberData.push({ x: dateTimestamp, y: returnToMemberAmount })
        cashWithdrawData.push({ x: dateTimestamp, y: cashWithdrawAmount })
        profitData.push({ x: dateTimestamp, y: dailyProfit })
      })

      return {
        categories: allDates.map((date) => moment(date).valueOf()),
        series: [
          { name: 'ฝากเงินสด', data: cashDepositData, color: '#10B981' },
          { name: 'ยอดโบนัสฝาก', data: bonusDepositData, color: '#F59E0B' },
          { name: 'คืนยอดเสีย', data: returnToMemberData, color: '#3B82F6' },
          { name: 'ถอนเงินสด', data: cashWithdrawData, color: '#EF4444' },
          { name: 'กำไรสุทธิ', data: profitData, color: '#8B5CF6' },
        ],
      }
    } catch (error) {
      console.error('❌ Error loading chart data:', error)
      return {
        categories: [],
        series: [],
      }
    }
  },
}
